import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Leaf, Truck, MessageCircle, Star, MapPin, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import ProductCard from '../components/ProductCard';

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes, zonesRes] = await Promise.all([
        supabase.from('products').select('*').eq('is_active', true).eq('is_featured', true).limit(8),
        supabase.from('categories').select('*').eq('is_active', true).order('sort_order'),
        supabase.from('delivery_zones').select('*').eq('is_active', true).order('sort_order'),
      ]);
      if (productsRes.data) setFeaturedProducts(productsRes.data);
      if (categoriesRes.data) setCategories(categoriesRes.data);
      if (zonesRes.data) setDeliveryZones(zonesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/foto1.jpeg')` }}
        />
        <div className="hero-overlay absolute inset-0" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-2xl"
          >
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-sm font-medium px-4 py-2 rounded-full mb-6">
              Desde 1985 · Chonchi, Chiloé
            </span>
            <h1 className="font-cabinet font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
              Productos Premium<br />Calidad y Atención
            </h1>
            <p className="text-lg text-white/90 mb-8 max-w-xl">
              Frutas, verduras, pan fresco artesanal y productos gourmet de la más alta calidad.
              Reparto a domicilio en toda la isla de Chiloé.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/productos"
                className="inline-flex items-center gap-2 bg-[#2C5530] hover:bg-[#3A6E40] text-white font-medium px-6 py-3 rounded-full transition-colors"
              >
                Ver Catálogo
                <ArrowRight size={18} />
              </Link>
              <a
                href="https://wa.me/56994798582?text=Hola!+Quiero+hacer+un+pedido"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold px-6 py-3 rounded-full transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Pedir por WhatsApp
              </a>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-white/20">
              <div>
                <span className="font-cabinet font-bold text-3xl text-white">35+</span>
                <span className="block text-sm text-white/70">Años de Experiencia</span>
              </div>
              <div>
                <span className="font-cabinet font-bold text-3xl text-white">100%</span>
                <span className="block text-sm text-white/70">Natural y Artesanal</span>
              </div>
              <div>
                <span className="font-cabinet font-bold text-3xl text-white">5+</span>
                <span className="block text-sm text-white/70">Zonas de Reparto</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pan Banner */}
      <section className="bg-[#E2B35B] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="text-4xl">🥖</span>
              <div>
                <h3 className="font-cabinet font-bold text-lg text-[#1A231C]">Pan Artesanal Fresco</h3>
                <p className="text-sm text-[#1A231C]/80">Recién horneado desde las <strong>8:00 am</strong>, todos los días · La mejor calidad</p>
              </div>
            </div>
            <a
              href="https://wa.me/56994798582?text=Hola!+Me+interesa+el+pan+fresco"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#1A231C] hover:bg-[#2C5530] text-white font-medium px-5 py-2 rounded-full transition-colors text-sm"
            >
              Reservar Pan Hoy
            </a>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#808E83]">Categorías</span>
            <h2 className="font-cabinet font-bold text-2xl sm:text-3xl text-[#1A231C] mt-2">Explora Nuestros Productos</h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/productos?categoria=${cat.id}`}
                className="group relative bg-[#F3F3EF] hover:bg-[#2C5530] rounded-xl overflow-hidden transition-all duration-300"
              >
                <div className="aspect-square overflow-hidden">
                  {cat.image ? (
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-5xl bg-[#F3F3EF]">
                      {cat.emoji}
                    </div>
                  )}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-4">
                  <h3 className="font-semibold text-sm text-white">{cat.name}</h3>
                  <p className="text-xs text-white/80">{cat.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#808E83]">Catálogo</span>
              <h2 className="font-cabinet font-bold text-2xl sm:text-3xl text-[#1A231C] mt-2">Nuestras Especialidades</h2>
            </div>
            <Link
              to="/productos"
              className="hidden sm:inline-flex items-center gap-2 text-[#2C5530] font-medium hover:gap-3 transition-all"
            >
              Ver todo
              <ArrowRight size={18} />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl h-72 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <div className="text-center mt-8 sm:hidden">
            <Link
              to="/productos"
              className="inline-flex items-center gap-2 bg-[#2C5530] hover:bg-[#3A6E40] text-white font-medium px-6 py-3 rounded-full transition-colors"
            >
              Ver todo el catálogo
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-cabinet font-bold text-2xl sm:text-3xl text-[#1A231C]">¿Por Qué Elegirnos?</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Leaf, title: '100% Natural', desc: 'Productos frescos sin conservantes artificiales' },
              { icon: Truck, title: 'Reparto a Domicilio', desc: 'Entregamos en Chonchi, Castro, Dalcahue, Quellón y Ancud' },
              { icon: MessageCircle, title: 'Pedidos por WhatsApp', desc: 'Haz tu pedido de forma simple y rápida' },
              { icon: Star, title: 'Calidad Garantizada', desc: 'Más de 35 años de frescura y calidad' },
            ].map((feature, i) => (
              <div key={i} className="text-center p-6 bg-[#F3F3EF] rounded-xl">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#2C5530] text-white rounded-xl mb-4">
                  <feature.icon size={24} />
                </div>
                <h3 className="font-cabinet font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-sm text-[#808E83]">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Info */}
      <section className="py-16 bg-[#2C5530] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Hours */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-8 h-8" />
                <h3 className="font-cabinet font-bold text-2xl">Horarios de Atención</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-4 bg-white/10 rounded-xl">
                  <span>Lunes a Sábado</span>
                  <strong>8:00 - 20:00</strong>
                </div>
                <div className="flex justify-between items-center p-4 bg-white/10 rounded-xl">
                  <span>Domingo</span>
                  <strong>9:00 - 19:00</strong>
                </div>
                <div className="flex justify-between items-center p-4 bg-[#E2B35B] text-[#1A231C] rounded-xl">
                  <span>🥖 Pan Fresco de la mejor calidad</span>
                  <strong>Desde las 8:00</strong>
                </div>
              </div>
            </div>

            {/* Zones */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-8 h-8" />
                <h3 className="font-cabinet font-bold text-2xl">Zonas de Reparto</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {deliveryZones.map((zone) => (
                  <div key={zone.id || zone.name} className="p-4 bg-white/10 rounded-xl">
                    <strong className="block">{zone.name}</strong>
                    <span className="text-sm text-white/70">{zone.delivery_day}</span>
                    {zone.delivery_cost > 0 && (
                      <span className="block text-xs text-[#E2B35B] mt-1">Envío: ${zone.delivery_cost.toLocaleString('es-CL')}</span>
                    )}
                    {zone.delivery_cost === 0 && zone.name === 'Chonchi' && (
                      <span className="block text-xs text-[#25D366] mt-1">Retiro gratis</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 p-4 bg-[#25D366] rounded-xl text-center">
                <span className="font-medium">🎁 Envío GRATIS en pedidos sobre $15.000</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
}
